import type { TableColumn } from '@/types/Base';

type TableKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${TableKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

export function sortData<T extends object>(
  data: T[],
  column: number,
  order: 'asc' | 'desc',
  headers: TableColumn[]
): T[] {
  if (column < 0 || column >= headers.length) {
    return data;
  }

  const sortKey = headers[column].value as TableKeyOf<T>;

  const resolveField = (item: any, field: string): any => {
    return field
      .split('.')
      .reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), item);
  };

  return data.sort((a, b) => {
    const originalValA = resolveField(a, sortKey);
    const originalValB = resolveField(b, sortKey);

    let compareValA: number | string;
    let compareValB: number | string;

    if (typeof originalValA === 'boolean') {
      compareValA = originalValA ? 1 : 0;
      compareValB = originalValB ? 1 : 0;
    } else if (!isNaN(Number(originalValA)) && !isNaN(Number(originalValB))) {
      compareValA = Number(originalValA);
      compareValB = Number(originalValB);
    } else {
      compareValA = String(originalValA).toLowerCase();
      compareValB = String(originalValB).toLowerCase();
    }

    if (compareValA < compareValB) {
      return order === 'asc' ? -1 : 1;
    }
    if (compareValA > compareValB) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });
}

export function filterData<T extends object>(
  data: T[],
  query: string,
  searchableFields: string[]
): T[] {
  if (!query) return data;
  query = query.toLowerCase();

  const nestedField = (item: any, field: string): any => {
    return field
      .split('.')
      .reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), item);
  };

  return data.filter((item) =>
    searchableFields.some((field) => {
      const value = nestedField(item, field as string);
      return value && String(value).toLowerCase().includes(query);
    })
  );
}

export function filterActive<T extends { isActive?: boolean }>(data: T[], isActive?: boolean): T[] {
  if (isActive) return data.filter((x: T) => x.isActive);
  return data.filter((x: T) => !x.isActive);
}
